import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const linkClass = (path: string) =>
    `relative text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
      scrolled
        ? isActive(path)
          ? "text-primary-600"
          : "text-foreground-700 hover:text-primary-500"
        : isActive(path)
          ? "text-white"
          : "text-white/80 hover:text-white"
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-200 ${
                scrolled ? "bg-primary-500" : "bg-white/20 backdrop-blur-sm"
              }`}
            >
              <i className="ri-plug-fill text-lg text-white"></i>
            </div>
            <span
              className={`text-lg font-bold font-heading transition-colors duration-200 ${
                scrolled ? "text-foreground-900" : "text-white"
              }`}
            >
              {t("site.name")}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link to="/" className={`px-3 py-2 rounded-lg ${linkClass("/")}`}>
              {t("nav.home")}
            </Link>
            <Link
              to="/category/industrial-connectors"
              className={`px-3 py-2 rounded-lg ${linkClass("/category")}`}
            >
              {t("nav.products")}
            </Link>
            <Link
              to="/articles"
              className={`px-3 py-2 rounded-lg ${linkClass("/articles")}`}
            >
              {t("nav.articles")}
            </Link>
            <Link
              to="/about"
              className={`px-3 py-2 rounded-lg ${linkClass("/about")}`}
            >
              {t("nav.about")}
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/about"
              className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                scrolled
                  ? "bg-primary-500 text-white hover:bg-primary-600"
                  : "bg-white/20 backdrop-blur-sm text-white hover:bg-white/30"
              }`}
            >
              {t("nav.contact")}
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
              scrolled
                ? "text-foreground-700 hover:bg-background-200"
                : "text-white hover:bg-white/10"
            }`}
            aria-label="Toggle menu"
          >
            <i
              className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}
            ></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-4 py-3 flex flex-col gap-1">
            <Link
              to="/"
              className={`px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                isActive("/")
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {t("nav.home")}
            </Link>
            <Link
              to="/category/industrial-connectors"
              className={`px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                isActive("/category")
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {t("nav.products")}
            </Link>
            <Link
              to="/articles"
              className={`px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                isActive("/articles")
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {t("nav.articles")}
            </Link>
            <Link
              to="/about"
              className={`px-3 py-2.5 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                isActive("/about")
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              {t("nav.about")}
            </Link>
            <Link
              to="/about"
              className="mt-2 px-3 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium text-center cursor-pointer hover:bg-primary-600 transition-colors"
            >
              {t("nav.contact")}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}